// `unlock` advises before the operator unlocks an unconfirmed operation (P120); `unlocked` follows the unlock.
const copy = {
  initial: {
    action: "Generate Lucky Draw",
    failed: "Lucky Draw generation failed.",
    unknown: "We couldn’t confirm whether the Lucky Draw was saved",
    explanation: "The player positions may already be saved. Further draw changes are temporarily disabled to avoid repeating the draw.",
    unlock: "If the Lucky Draw is missing, unlock and generate it again. If it is there, unlock and continue. Unlocking repeats nothing.",
    unlocked: "If the Lucky Draw is missing, generate it again.",
  },
  redraw: {
    action: "Redraw Lucky Draw",
    failed: "Lucky Draw redraw failed.",
    unknown: "We couldn’t confirm whether the Lucky Draw was saved",
    explanation: "The new player positions may already be saved. Further draw changes are temporarily disabled to avoid repeating the redraw.",
    unlock: "If the positions did not change, unlock and redraw again. If they changed, unlock and continue. Unlocking repeats nothing.",
    unlocked: "If the positions did not change, redraw again.",
  },
  swap: {
    action: "Swap player positions",
    failed: "The position swap failed.",
    unknown: "We couldn’t confirm whether the position swap was saved",
    explanation: "The selected players or teams may already have exchanged positions. Further draw changes are temporarily disabled to avoid repeating the swap.",
    unlock: "If the players did not exchange positions, unlock and swap them again. If they did, unlock and continue. Unlocking repeats nothing.",
    unlocked: "If the players did not exchange positions, swap them again.",
  },
  generate: {
    action: "Generate Main Draw",
    failed: "Main Draw generation failed.",
    unknown: "We couldn’t confirm whether the Main Draw was generated",
    explanation: "The matches may already have been created. Further draw changes are temporarily disabled to avoid generating the Main Draw again.",
    unlock: "If the Main Draw is missing, unlock and generate it again. If it is there, unlock and continue. Unlocking repeats nothing.",
    unlocked: "If the Main Draw is missing, generate it again.",
  },
}

export const drawFeedbackCopy = action => copy[action] || copy.initial

// P120: what CP found about an operation it could not confirm, one sentence per source.
const auditOutcomes = { confirmed: "Confirmed", failed: "Failed", pending: "Pending confirmation" }
const auditOutcome = outcome => auditOutcomes[outcome] || "Unknown result"
export const drawAnswerFinding = answer => {
  if (answer === "accepted") return "Accepted by the server"
  if (answer === "none") return "No answer (the connection dropped or timed out)"
  if (answer === "unexpected") return "An unexpected answer from the server"
  const status = /^error:(\d{3})$/.exec(answer || "")?.[1]
  return status ? `Error before the request was accepted (HTTP ${status})` : "Not recorded"
}
// `time` formats a recorded moment for the operator.
export const drawLogsFinding = (logs, time) => {
  if (logs?.kind === "none") return "No record of this request"
  if (logs?.kind === "other") return "A record that does not match this request"
  if (logs?.kind === "multiple") return `${logs.total} records for this request: ${logs.outcomes.map(auditOutcome).join(", ")}`
  if (logs?.kind === "recorded") return logs.outcome === "pending" && logs.since ? `Pending confirmation since ${time(logs.since)}` : auditOutcome(logs.outcome)
  return "Couldn’t be read. Check draw status again."
}
export const drawMatchesFinding = matches => !Number.isSafeInteger(matches) ? "Couldn’t be read. Open Main Draw to check it."
  : matches === 0 ? "No matches" : matches === 1 ? "1 match" : `${matches} matches`

const phaseOperationRunning = "A phase operation is still running. Check its result in Phases, then try again."
// P51 point 5 (Tudor, 2026-10-25: warn, do not block): a redraw, a swap or a Main Draw generation over recorded results waits for
// an explicit confirmation; the server counts the results (`data.results`).
export const RESULTS_LOSS = "DRAW_LOCKED_BY_RESULTS"
const results = count => count === 1 ? "1 result" : `${count} results`
export const resultsLossMessage = (action, count) => {
  const known = Number.isSafeInteger(count) && count > 0
  if (action === "generate") return known
    ? `${results(count)} will be lost: the Main Draw is generated again and the matches with results are deleted.`
    : "The entered results will be lost: the Main Draw is generated again and the matches with results are deleted."
  return `This competition already has ${known ? results(count) : "results"}. The Lucky Draw changes; the ${count === 1 ? "result" : "results"} and the matches stay on the old draw until the Main Draw is generated again.`
}
export const resultsLossConfirmLabel = action => ({ generate: "Yes, generate again", swap: "Yes, swap the positions" })[action] || "Yes, redraw"
// Each reason follows the action's failure sentence ("Main Draw generation failed. …"), so it must read after any action.
const failureReasons = {
  LUCKY_DRAW_REQUIRED: "Generate Lucky Draw from Entries before generating Main Draw.",
  DRAW_PHASE_NOT_FOUND: "The selected phase no longer exists. Reload the competition and choose an available phase.",
  COMPETITION_NOT_FOUND: "The competition could not be found. Return to the competition list and reopen it.",
  EVENT_NOT_FOUND: "The event could not be found. Return to the event list and reopen it.",
  FAILED_TO_FETCH_SORTED_PLAYERS: "The Main List has no active entries. Add players or teams to the Main List first.",
  ERROR_PERMISSIONS: "You do not have permission to perform this action. Contact an administrator.",
  COULD_NOT_CREATE_MATCH: "The matches could not be created. Review Action Logs before trying again.",
  PHASE_REVIEW_REQUIRED: "Players are already placed in this competition's phase. Reset the phase from Phases before generating the Main Draw again.",
  PHASE_POINTS_FINALIZED: "The phase points have been finalized, so the Main Draw can no longer be generated again.",
  DRAW_LOCKED_BY_RESULTS: "Results are already recorded in this competition, so the change waited for a confirmation.",
  // Rare, on a redraw or a swap without confirmation: the server could not count the results, so it changed nothing.
  DRAW_LOCK_UNAVAILABLE: "The competition's results could not be counted, so nothing changed. Try again.",
  ENTRY_MOVE_OPERATION_IN_PROGRESS: "An entry operation is still running in this competition. Wait for it to finish in Entries, then try again.",
  EVENT_COMPLETION_OPERATION_IN_PROGRESS: "The event is being completed. Wait for completion to finish before changing the draw.",
  NON_ADMISSION_OPERATION_IN_PROGRESS: "A non-admission is being recorded for this competition. Wait for it to finish, then try again.",
  PHASE_OPERATION_IN_PROGRESS: phaseOperationRunning,
  INVALID_COMPETITION_PHASE_OPERATION_PENDING: phaseOperationRunning,
  TOURNAMENT_REVIEW_IN_PROGRESS: "A tournament review is being prepared for this event. Try again in a moment.",
  PENDING_ON_STAGE_0_ERROR: "A qualifier placeholder cannot be moved into Qualifications.",
  DRAW_POSITION_NOT_FOUND: "One of the selected positions changed. Reload the Lucky Draw and select the positions again.",
  REQUEST_REJECTED: "The request was rejected. Refresh and review the current competition.",
  SESSION_EXPIRED: "Your session expired, so nothing was changed. Sign in again, then repeat the action.",
  // P120: the gateway could not check the session in time and refused the request before sending it.
  SERVICE_UNAVAILABLE: "The server was briefly unavailable, so nothing was sent. Try again in a moment.",
  // Events' catch-all for an unexpected error (and, once the API sends codes only, for a database error).
  SOMETHING_WENT_WRONG: "The server could not complete it. Reload the competition and check the draw before trying again.",
}
// The sentence for a draw failure code, or null. Only known codes are worded: driver or free text is never shown.
export const drawFailureReason = code =>
  typeof code === "string" && Object.prototype.hasOwnProperty.call(failureReasons, code) ? failureReasons[code] : null
export const drawFailureMessage = (action, failure) =>
  `${drawFeedbackCopy(action).failed} ${drawFailureReason(failure) || "Review Action Logs before trying again."}`

export const drawLockReason = operation => !operation?.locked ? undefined : operation.status === "unknown"
  ? "Check draw status before making further changes."
  : operation.status === "blocked" ? "Draw changes are blocked. Review the message above before continuing."
    : "Wait for the current draw operation to finish before making further changes."
